/**
 * The "تسجيل حوالة جديدة" form container (C23, B4 — D05 §2, §5): binds `TransferFormModal` and the old warning dialogs
 * to `/transfers/*`.
 *
 *  - Code (D-087): an outgoing transfer gets a server-issued code when the form opens or "جينيريت" is pressed, shown
 *    read-only; an incoming one types the sender's code.
 *  - Commission calculator (old R-3): every change of amount / rate / commission / total / fee asks the server
 *    (`POST /transfers/quote`) — the old two-way binding without float arithmetic in the browser (ADR-02 §2.9).
 *  - Submit: the old required-field messages first (CashTransfers.tsx:708-733, :782-819), then the server preview with
 *    the record's details; its refusals open the old dialogs: the vault warning (cash actually leaving, D-014 / T-17),
 *    the trust warning (D-084, T-10), the partner overflow (out: the rest as the partner's trust, D-078; in: as its debt,
 *    D-082), the customer excess dialogs of C22 (an incoming payment beyond a debt, D-073). An incoming code already
 *    recorded from the same partner opens the duplicate-code warning (D-087). Then PIN (own or supervisor, D-018) →
 *    commit; "تسجيل مع طباعة" prints the voucher of the posted transfer (D-064 (2), D-147).
 *  - Foreign currencies (step 3): IQD / USD settlement as a currency trade (D-085 (a)) with the settlement rate pre-filled
 *    from the approved table (out = sell, in = buy; USD = the quote ÷ the approved USD buy rate, from the server) and
 *    the cost pre-filled with the stock average (D-065, D-016); or the currency itself (D-085 (b)) with the valuation
 *    rate pre-filled from the approved buy rate. The settlement cash shown is the server preview's until typed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  TransferDuplicateCodeWarning,
  TransferFormModal,
  TransferOfficeWarning,
  TransferTrustWarning,
  TransferVaultWarning,
  type CtBalanceAlert,
  type CtCurrencyChoice,
  type CtDirection,
  type CtFormField,
  type CtFormValues,
  type CtPayment,
  type CtSettlement,
  type CtTab,
  type PartnerOfficeOption,
} from "@faylasuf/ui";
import type { AccountRow, NewCustomerAccountSpec, TransferPartyData, TransferPreviewBody, TransferPreviewResult, TransfersSummary } from "../../api/types.js";
import { isApiClientError } from "../../api/client.js";
import { useApp, useSession } from "../../app/context.js";
import { useExcessFlow } from "../../app/customer-picker.js";
import { engineErrors, errorMessage, isSessionLost } from "../../app/errors.js";
import { useGateway } from "../../app/operations.js";
import { useToast } from "../../app/toast.js";
import { decAdd, decSub, isZero } from "../customers/format.js";
import { amountWith, currencyName, flagOf, gt, lte0, money2, symbolOf } from "./format.js";

const EMPTY: CtFormValues = {
  clientName: "", clientDocument: "", destination: "", code: "",
  amount: "", commissionRate: "1000", partnerFee: "", commission: "", totalAmount: "",
  settlementRate: "", settlementCash: "", costRate: "", valuationRate: "",
};

type Warning =
  | { readonly k: "vault"; readonly vaultName: string; readonly available: string; readonly required: string; readonly symbol: string }
  | { readonly k: "trust"; readonly name: string; readonly available: string; readonly required: string; readonly symbol: string }
  | { readonly k: "office"; readonly mode: "out" | "in"; readonly name: string; readonly available: string; readonly surplus: string; readonly symbol: string; readonly party: TransferPartyData; readonly currency: "IQD" | "USD"; readonly print: boolean }
  | { readonly k: "duplicate"; readonly pv: TransferPreviewResult; readonly print: boolean };

interface Extra {
  readonly partnerSurplus?: { readonly kind: "account"; readonly accountId: string };
  readonly paymentSurplus?: { readonly kind: "cash" } | { readonly kind: "account"; readonly accountId: string };
  readonly newAccounts?: readonly NewCustomerAccountSpec[];
}

const lineOf = (p: TransferPartyData, cur: "IQD" | "USD") => (cur === "USD" ? p.usd : p.iqd);

export function FormContainer(props: {
  readonly open: boolean;
  readonly data: TransfersSummary;
  readonly tab: CtTab;
  readonly onTabChange: (tab: CtTab) => void;
  readonly onClose: () => void;
  /** After a posted "record & print": open the details window and print. */
  readonly onPrint: (transferId: string) => void;
}) {
  const { t, source, locale } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const gateway = useGateway();
  const qc = useQueryClient();
  const { data, tab } = props;
  const currencies = data.currencies;

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [currency, setCurrency] = useState("IQD");
  const [direction, setDirection] = useState<CtDirection>("sent");
  const [values, setValues] = useState<CtFormValues>(EMPTY);
  const [payment, setPayment] = useState<CtPayment>("cash");
  const [customerId, setCustomerId] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [payer, setPayer] = useState<"partner" | "beneficiary">("partner");
  const [settlement, setSettlement] = useState<CtSettlement>("IQD");
  const [cashTyped, setCashTyped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState<Warning | null>(null);
  const [step3Preview, setStep3Preview] = useState<TransferPreviewResult | null>(null);
  const issuedCode = useRef<string | null>(null);
  const quoteSeq = useRef(0);

  const isForeign = currency !== "IQD" && currency !== "USD";
  const fee = values.partnerFee;
  const showPartner = tab === "office" || gt(fee || "0", "0");
  const route: "vault" | "partner" = showPartner ? "partner" : "vault";
  const dir = direction === "sent" ? "out" : "in";
  const partner = data.parties.find((p) => p.id === partnerId) ?? null;
  const customer = data.parties.find((p) => p.id === customerId) ?? null;
  const curInfo = currencies.find((c) => c.code === currency);
  const settleCur: "IQD" | "USD" = settlement === "USD" ? "USD" : "IQD";
  /** The party lines' currency: the transfer currency (IQD / USD) or, settled, the settlement currency. */
  const partyCur: "IQD" | "USD" = isForeign ? settleCur : (currency as "IQD" | "USD");

  const issueCode = useCallback(async () => {
    try {
      const r = await source.transferCode();
      issuedCode.current = r.code;
      setValues((v) => ({ ...v, code: r.code }));
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
    }
  }, [source, sessionLost, toast, t]);

  // open → reset (old effect :423-444) and issue a code (old `generateRandomCode()` on the register button :940)
  useEffect(() => {
    if (!props.open) return;
    setStep(1);
    setCurrency("IQD");
    setDirection("sent");
    setValues(EMPTY);
    setPayment("cash");
    setCustomerId("");
    setPartnerId("");
    setPayer("partner");
    setSettlement("IQD");
    setCashTyped(false);
    setError(null);
    setStep3Preview(null);
    issuedCode.current = null;
    void issueCode();
  }, [props.open, issueCode]);

  // ---------------------------------------------------------------- calculator (server, old R-3)
  const requote = useCallback(
    async (next: CtFormValues, source0: "rate" | "commission" | "total" | "keep", ctx: { cur: string; dir: "out" | "in"; route: "vault" | "partner"; payer: "partner" | "beneficiary" }) => {
      const seq = ++quoteSeq.current;
      if (!next.amount || lte0(next.amount)) {
        if (source0 === "rate") setValues((v) => ({ ...v, commission: "", totalAmount: "" }));
        return;
      }
      try {
        const base = { currency: ctx.cur, direction: ctx.dir, route: ctx.route, amount: next.amount, ...(next.partnerFee ? { partnerFee: next.partnerFee } : {}), commissionPayer: ctx.payer };
        const body =
          source0 === "rate" ? { ...base, commissionRate: next.commissionRate || "0" }
          : source0 === "total" ? { ...base, total: next.totalAmount || "0" }
          : { ...base, commission: next.commission || "0" };
        const r = await source.transferQuote(body);
        if (seq !== quoteSeq.current) return;
        setValues((v) => (source0 === "total" ? { ...v, commission: r.commission } : { ...v, commission: source0 === "rate" ? r.commission : v.commission, totalAmount: r.total }));
      } catch (e) {
        if (isSessionLost(e)) sessionLost();
      }
    },
    [source, sessionLost],
  );
  const qctx = { cur: currency, dir: dir as "out" | "in", route, payer };

  const onChange = (field: CtFormField, raw: string) => {
    const next = { ...values, [field]: raw };
    setValues(next);
    if (field === "amount" || field === "commissionRate") void requote(next, "rate", qctx);
    else if (field === "commission" || field === "partnerFee") void requote(next, "keep", { ...qctx, route: field === "partnerFee" && gt(raw || "0", "0") ? "partner" : qctx.route });
    else if (field === "totalAmount") void requote(next, "total", qctx);
    else if (field === "settlementCash") setCashTyped(raw !== "");
  };

  // the total's meaning follows direction / route / payer (D-248 (c))
  useEffect(() => {
    if (props.open && values.amount) void requote(values, "keep", qctx);
  }, [direction, route, payer]);

  const onCurrency = (code: string) => {
    setCurrency(code);
    // old effect :447-453: amounts reset, default rate 1000 (IQD per million) or 10 (per 1,000)
    setValues((v) => ({ ...v, amount: "", commission: "", partnerFee: "", totalAmount: "", commissionRate: code === "IQD" ? "1000" : "10", settlementRate: "", settlementCash: "", costRate: "", valuationRate: "" }));
    setCashTyped(false);
    setStep3Preview(null);
  };

  const onDirection = (d: CtDirection) => {
    setDirection(d);
    if (d === "received") setValues((v) => ({ ...v, code: "" }));
    else if (issuedCode.current) setValues((v) => ({ ...v, code: issuedCode.current! }));
    else void issueCode();
  };

  // ---------------------------------------------------------------- pickers
  const [customerFilter, setCustomerFilter] = useState("");
  const customerOptions = useMemo(() => {
    const kind = payment === "credit" ? "debt" : "trust";
    return data.parties
      .filter((p) => p.kind === kind)
      .map((p) => ({
        id: p.id,
        label: t("ct.client_option", {
          number: p.accountNumber,
          name: p.name,
          kind: t(p.kind === "debt" ? "ct.ledger_debt" : "ct.ledger_trust"),
          balance: data.usdHidden ? money2(p.iqd.balance) : `${money2(p.iqd.balance)} / $${money2(p.usd.balance)}`,
        }),
      }));
  }, [data.parties, data.usdHidden, payment, t]);
  const onCustomerSearch = (text: string) => {
    setCustomerFilter(text);
    const val = text.trim().toLowerCase();
    if (!val) return;
    const kind = payment === "credit" ? "debt" : "trust";
    const found = data.parties.filter((p) => p.kind === kind).find((p) => p.accountNumber.toLowerCase() === val || String(p.number) === val || p.name.toLowerCase().includes(val));
    if (found) setCustomerId(found.id);
  };
  void customerFilter;

  const partnerOptions: PartnerOfficeOption[] = useMemo(
    () =>
      data.parties.map((p) => ({
        id: p.id,
        label: p.name,
        kind: p.kind,
        phone: p.phone,
        accountLabel: p.accountNumber,
        balanceLabel: money2(p.iqd.balance),
        usdBalanceLabel: data.usdHidden || isZero(p.usd.balance) ? null : money2(p.usd.balance),
      })),
    [data.parties, data.usdHidden],
  );
  const filteredPartners = useMemo(() => {
    const q = partnerSearch.trim().toLowerCase();
    if (!q) return partnerOptions;
    return partnerOptions.filter((o) => o.label.toLowerCase().includes(q) || (o.phone ?? "").includes(q) || o.accountLabel.toLowerCase().includes(q));
  }, [partnerOptions, partnerSearch]);

  // ---------------------------------------------------------------- currencies and balances
  const balanceOf = (code: string): string => (code === "IQD" ? (data.banner.iqd?.balance ?? "0") : code === "USD" ? (data.banner.usd?.balance ?? "0") : (currencies.find((c) => c.code === code)?.stock?.balance ?? "0"));
  const choices: CtCurrencyChoice[] = [
    { code: "IQD", flag: flagOf("IQD"), label: t("ct.cur_iqd"), symbol: "د.ع", balanceLabel: money2(balanceOf("IQD")), zero: lte0(balanceOf("IQD")) },
    ...(data.usdHidden ? [] : [{ code: "USD", flag: flagOf("USD"), label: t("ct.cur_usd"), symbol: "$", balanceLabel: money2(balanceOf("USD")), zero: lte0(balanceOf("USD")) }]),
    ...currencies.map((c) => ({ code: c.code, flag: flagOf(c.code), label: locale === "ar" ? c.nameAr : c.nameEn, symbol: c.symbol, balanceLabel: money2(balanceOf(c.code)), zero: lte0(balanceOf(c.code)) })),
  ];

  const sym = symbolOf(currency, currencies);
  const curName = currencyName(currency, currencies, t, locale);
  let balanceAlert: CtBalanceAlert | null = null;
  if (payment === "cash") {
    const bal = balanceOf(currency);
    // D05 T-17: only an incoming transfer through a partner pays cash out of the vault without the courier bringing it.
    const leaving = dir === "in" && route === "partner" ? values.totalAmount || values.amount : "0";
    const base = { currencyName: curName, symbol: sym, balanceLabel: money2(bal), requiredLabel: money2(leaving || "0"), deficitLabel: money2(decSub(leaving || "0", bal)) };
    if (lte0(bal) && gt(leaving || "0", "0")) balanceAlert = { state: "zero", ...base };
    else if (gt(leaving || "0", bal)) balanceAlert = { state: "deficit", ...base };
    else balanceAlert = { state: "ok", ...base };
  }

  // ---------------------------------------------------------------- engine input
  const cashVault = (cur: "IQD" | "USD") => (cur === "USD" ? data.banner.usd?.accountId : data.banner.iqd?.accountId);
  const buildBody = (extra: Extra = {}): TransferPreviewBody | string => {
    const details = { beneficiaryName: values.clientName.trim(), documentId: values.clientDocument.trim(), destination: values.destination.trim(), code: values.code.trim() };
    const commission = values.commission || "0";
    const common: Record<string, unknown> = { currency, amount: values.amount, commission, ...(values.partnerFee ? { partnerFee: values.partnerFee } : {}) };
    if (route === "partner") {
      if (!partner) return t("ct.err_partner");
      common["partnerAccountId"] = lineOf(partner, partyCur).accountId;
      if (dir === "in") common["commissionPayer"] = payer;
      if (extra.partnerSurplus) common["partnerSurplus"] = extra.partnerSurplus;
    }
    if (payment !== "cash") {
      if (!customer) return t("ct.err_client");
      common["payment"] = { kind: "account", accountId: lineOf(customer, partyCur).accountId, ...(extra.paymentSurplus ? { surplus: extra.paymentSurplus } : {}) };
    } else common["payment"] = { kind: "cash" };
    const newAccounts = extra.newAccounts && extra.newAccounts.length > 0 ? { newAccounts: extra.newAccounts } : {};
    if (!isForeign) {
      const v = cashVault(currency as "IQD" | "USD");
      return { type: dir === "out" ? "transfer_out" : "transfer_in", input: { ...common, ...(v ? { cashAccountId: v } : {}) }, details, ...newAccounts };
    }
    if (settlement === "SELF") {
      return { type: "transfer_currency_passthrough", input: { ...common, direction: dir, valuationRate: values.valuationRate }, details, ...newAccounts };
    }
    const v = cashVault(settleCur);
    const settle: Record<string, unknown> = { currency: settleCur, rate: values.settlementRate, ...(v ? { cashAccountId: v } : {}) };
    if (cashTyped && values.settlementCash) settle["total"] = values.settlementCash;
    if (dir === "out") settle["costRate"] = values.costRate;
    return { type: dir === "out" ? "transfer_out_settled" : "transfer_in_settled", input: { ...common, settlement: settle }, details, ...newAccounts };
  };

  /** The old required-field checks (:708-733), in the old order and wording. */
  const validateStep2 = (): string | null => {
    if (!values.clientName.trim()) return t("ct.err_name");
    if (!values.destination.trim()) return t("ct.err_dest");
    if (!values.code.trim()) return t("ct.err_code");
    if (!values.clientDocument.trim()) return t("ct.err_doc");
    if (!values.amount || lte0(values.amount)) return t("ct.err_amount");
    if (showPartner && !partner) return t("ct.err_partner");
    if ((payment === "credit" || payment === "trust") && !customer) return t("ct.err_client");
    return null;
  };

  // ---------------------------------------------------------------- step 3 (foreign)
  const prefillStep3 = (s: CtSettlement) => {
    const c = curInfo;
    if (!c) return;
    const r = s === "USD" ? c.ratesUsd : c.rates;
    const rate = r ? (dir === "out" ? r.sell : r.buy) : "";
    setValues((v) => ({
      ...v,
      settlementRate: s === "SELF" ? v.settlementRate : rate,
      costRate: s === "USD" ? (c.costPrefillUsd ?? "") : (c.costPrefill ?? ""),
      valuationRate: c.rates?.buy ?? "",
      settlementCash: "",
    }));
    setCashTyped(false);
  };
  const onNext = () => {
    setError(null);
    const msg = validateStep2();
    if (msg) return setError(msg);
    if (route === "partner") return setError(t("error.TRANSFER_PARTY_CURRENCY_UNSUPPORTED")); // D-249 (a)
    prefillStep3(settlement);
    setStep(3);
  };
  const onSettlement = (s: CtSettlement) => {
    setSettlement(s);
    if (s === "SELF" && payment !== "cash") setError(t("error.TRANSFER_PARTY_CURRENCY_UNSUPPORTED"));
    else setError(null);
    prefillStep3(s);
  };

  // the step-3 figures come from the server preview (settlement cash, commission at the rate)
  useEffect(() => {
    if (!props.open || step !== 3) return;
    const body = buildBody();
    if (typeof body === "string") return;
    const h = setTimeout(() => {
      source
        .previewTransfer(body)
        .then((pv) => {
          setStep3Preview(pv);
          setError(null);
          if (!cashTyped && pv.derived["settlement_total"]) setValues((v) => ({ ...v, settlementCash: pv.derived["settlement_total"] ?? "" }));
        })
        .catch((e) => {
          setStep3Preview(null);
          if (isSessionLost(e)) sessionLost();
          else setError(errorMessage(t, e));
        });
    }, 350);
    return () => clearTimeout(h);
  }, [props.open, step, settlement, values.settlementRate, values.costRate, values.valuationRate, values.settlementCash, cashTyped]);

  // ---------------------------------------------------------------- submit
  const excess = useExcessFlow({
    accounts: useMemo(() => partiesAsAccounts(data.parties), [data.parties]),
    onReduce: () => undefined,
    onResolved: (res) => void submit(false, { paymentSurplus: res.surplus, newAccounts: res.newAccounts }),
  });

  const commit = async (pv: TransferPreviewResult, ack: boolean, print: boolean) => {
    setBusy(true);
    try {
      const total = amountWith(pv.cashTotal || values.totalAmount || values.amount, currency, currencies);
      const res = await gateway.pin.withPin((proof) => source.commitTransfer(pv.previewId, crypto.randomUUID(), ack, proof), { message: t("ct.pin_message", { code: pv.code, total }), allowSupervisor: true });
      if (!res) return;
      toast(t(res.status === "pending" ? "toast.sent_for_approval" : "toast.operation_posted"), res.status === "pending" ? "info" : "success");
      for (const k of ["transfers", "accounts", "dashboard", "currencies", "dollar", "customers", "approvals", "notifications"]) void qc.invalidateQueries({ queryKey: [k] });
      props.onClose();
      if (print && res.status === "posted") props.onPrint(res.transferId);
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else setError(errorMessage(t, e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (print: boolean, extra: Extra = {}) => {
    setError(null);
    const msg = validateStep2();
    if (msg) return setError(msg);
    if (isForeign) {
      if (route === "partner" || (settlement === "SELF" && payment !== "cash")) return setError(t("error.TRANSFER_PARTY_CURRENCY_UNSUPPORTED"));
      if (settlement === "SELF" ? !values.valuationRate || lte0(values.valuationRate) : !values.settlementRate || lte0(values.settlementRate)) return setError(t("ct.err_rate"));
      if (settlement !== "SELF" && dir === "out" && (!values.costRate || lte0(values.costRate))) return setError(t("ct.err_cost"));
    }
    const body = buildBody(extra);
    if (typeof body === "string") return setError(body);
    setBusy(true);
    let pv: TransferPreviewResult;
    try {
      pv = await source.previewTransfer(body);
    } catch (e) {
      setBusy(false);
      if (isSessionLost(e)) return sessionLost();
      return refusal(e, print);
    }
    setBusy(false);
    if (pv.duplicateCode && pv.duplicateCode.length > 0) return setWarning({ k: "duplicate", pv, print });
    await commit(pv, false, print);
  };

  /** Maps the server's refusal to the old dialogs (the server's figures, D-014). */
  const refusal = (e: unknown, print: boolean) => {
    const errs = engineErrors(e);
    const ins = errs.find((x) => x.code === "BALANCE_INSUFFICIENT");
    const vaultIds = new Set([data.banner.iqd?.accountId, data.banner.usd?.accountId].filter(Boolean));
    if (ins) {
      const acc = String(ins.params["account"] ?? "");
      const avail = String(ins.params["available"] ?? "0");
      const req = String(ins.params["requested"] ?? "0");
      const cur = String(ins.params["currency"] ?? currency);
      if (customer && (customer.iqd.accountId === acc || customer.usd.accountId === acc)) return setWarning({ k: "trust", name: customer.name, available: avail, required: req, symbol: symbolOf(cur, currencies) });
      if (vaultIds.has(acc) || !data.parties.some((p) => p.iqd.accountId === acc || p.usd.accountId === acc)) {
        const vaultName = cur === "IQD" ? t("ct.vault_name_iqd") : cur === "USD" ? t("ct.vault_name_usd") : t("ct.vault_name_cur", { name: currencyName(cur, currencies, t, locale) });
        return setWarning({ k: "vault", vaultName, available: avail, required: req, symbol: symbolOf(cur, currencies) });
      }
    }
    const sur = errs.find((x) => x.code === "SURPLUS_ACCOUNT_REQUIRED");
    if (sur) {
      const acc = String(sur.params["account"] ?? "");
      const surplus = String(sur.params["surplus"] ?? "0");
      if (partner && (partner.iqd.accountId === acc || partner.usd.accountId === acc)) {
        const line = lineOf(partner, partyCur);
        return setWarning({ k: "office", mode: dir, name: partner.name, available: line.balance, surplus, symbol: symbolOf(partyCur, currencies), party: partner, currency: partyCur, print });
      }
      if (customer && (customer.iqd.accountId === acc || customer.usd.accountId === acc)) {
        const row = partiesAsAccounts([customer]).find((a) => a.id === acc);
        if (row) return excess.open({ account: row, surplus });
      }
    }
    if (isApiClientError(e) && e.code === "TRANSFER_CODE_INVALID" && dir === "out") void issueCode();
    setError(errorMessage(t, e));
  };

  const confirmOffice = () => {
    if (!warning || warning.k !== "office") return;
    const w = warning;
    setWarning(null);
    // out: the rest goes to the partner's trust (old "قيد الباقي كأمانة", D-078); in: to its debt (D-082). Same person, opened if missing (D-017).
    const kind = w.mode === "out" ? "trust" : "debt";
    const own = data.parties.find((p) => p.personId === w.party.personId && p.kind === kind);
    if (own) return void submit(w.print, { partnerSurplus: { kind: "account", accountId: lineOf(own, w.currency).accountId } });
    void submit(w.print, { partnerSurplus: { kind: "account", accountId: "new:ps" }, newAccounts: [{ ref: "ps", kind, currency: w.currency, personId: w.party.personId }] });
  };

  // ---------------------------------------------------------------- step-3 summary labels
  const d3 = step3Preview?.derived ?? {};
  const settleSym = settlement === "USD" ? "$" : settlement === "IQD" ? "د.ع" : sym;
  const summary = {
    principalLabel: `${money2(values.amount || "0")} ${sym}`,
    commissionLabel: `${money2(values.commission || "0")} ${sym}`,
    commissionConvertedLabel:
      settlement === "SELF" ? (d3["commission_value_iqd"] ? `${money2(d3["commission_value_iqd"])} د.ع` : null)
      : payment !== "cash" && d3["commission_value"] ? `${money2(d3["commission_value"])} ${settleSym}` : null,
    partnerFeeLabel: values.partnerFee && gt(values.partnerFee, "0") ? `${money2(values.partnerFee)} ${sym}` : null,
    totalLabel: `${money2(values.totalAmount || values.amount || "0")} ${sym}`,
    cashFlowLabel: settlement === "SELF" ? `${money2(values.totalAmount || values.amount || "0")} ${sym}` : `${money2(values.settlementCash || "0")} ${settleSym}`,
  };
  const quoteUnit = curInfo?.quoteUnit ?? "1";
  const rateHeader = settlement === "SELF" ? `${quoteUnit} ${currency} = ${values.valuationRate || "0"} د.ع` : `${quoteUnit} ${currency} = ${values.settlementRate || "0"} ${settleSym}`;

  const vaultWarning = warning?.k === "vault" ? warning : null;
  const trustWarning = warning?.k === "trust" ? warning : null;
  const officeWarning = warning?.k === "office" ? warning : null;
  const dupWarning = warning?.k === "duplicate" ? warning : null;

  return (
    <>
      <TransferFormModal
        open={props.open}
        step={step}
        onStep={(s) => {
          setError(null);
          setStep(s);
        }}
        tab={tab}
        onTabChange={props.onTabChange}
        currencies={choices}
        currency={currency}
        onCurrency={onCurrency}
        isForeign={isForeign}
        direction={direction}
        onDirection={onDirection}
        values={values}
        onChange={onChange}
        codeEditable={direction === "received"}
        onGenerate={() => void issueCode()}
        payment={payment}
        onPayment={(p) => {
          setPayment(p);
          setCustomerId(""); // old effect :456-458
        }}
        customerOptions={customerOptions}
        customerId={customerId}
        onCustomer={setCustomerId}
        onCustomerSearch={onCustomerSearch}
        showPartner={showPartner}
        partner={{
          options: filteredPartners,
          selected: partnerOptions.find((o) => o.id === partnerId) ?? null,
          selectedId: partnerId,
          onSelect: (id) => setPartnerId(id),
          counts: { all: partnerOptions.length, debt: partnerOptions.filter((o) => o.kind === "debt").length, trust: partnerOptions.filter((o) => o.kind === "trust").length },
          searchQuery: partnerSearch,
          onSearchChange: setPartnerSearch,
          isOpen: partnerOpen,
          setIsOpen: setPartnerOpen,
        }}
        partnerIncoming={showPartner && direction === "received"}
        commissionPayer={payer}
        onCommissionPayer={setPayer}
        balanceAlert={balanceAlert}
        error={error}
        busy={busy}
        onNext={onNext}
        onSubmit={(print) => void submit(print)}
        onClose={props.onClose}
        settlement={settlement}
        onSettlement={onSettlement}
        usdSettlementAvailable={!data.usdHidden}
        iqdVaultLabel={money2(data.banner.iqd?.balance ?? "0")}
        usdVaultLabel={money2(data.banner.usd?.balance ?? "0")}
        selfStockLabel={`${money2(curInfo?.stock?.balance ?? "0")} ${sym}`}
        rateHeader={rateHeader}
        quoteUnitLabel={quoteUnit === "1" ? "1" : money2(quoteUnit).replace(/\.00$/, "")}
        showCost={settlement !== "SELF" && dir === "out"}
        summary={summary}
      />
      <TransferVaultWarning
        open={vaultWarning !== null}
        vaultName={vaultWarning?.vaultName ?? ""}
        availableLabel={vaultWarning ? `${money2(vaultWarning.available)} ${vaultWarning.symbol}` : ""}
        requiredLabel={vaultWarning ? `${money2(vaultWarning.required)} ${vaultWarning.symbol}` : ""}
        deficitLabel={vaultWarning ? `${money2(decSub(vaultWarning.required, vaultWarning.available))} ${vaultWarning.symbol}` : ""}
        canSwitch={payment === "cash" && dir === "in"}
        onSwitchCredit={() => {
          setWarning(null);
          setPayment("credit");
          setCustomerId("");
        }}
        onSwitchTrust={() => {
          setWarning(null);
          setPayment("trust");
          setCustomerId("");
        }}
        onClose={() => setWarning(null)}
      />
      <TransferTrustWarning
        open={trustWarning !== null}
        clientName={trustWarning?.name ?? ""}
        availableLabel={trustWarning ? `${money2(trustWarning.available)} ${trustWarning.symbol}` : ""}
        requiredLabel={trustWarning ? `${money2(trustWarning.required)} ${trustWarning.symbol}` : ""}
        onChooseOther={() => {
          setWarning(null);
          setCustomerId("");
        }}
        onCancelAll={() => {
          setWarning(null);
          props.onClose();
        }}
      />
      <TransferOfficeWarning
        open={officeWarning !== null}
        mode={officeWarning?.mode ?? "out"}
        officeName={officeWarning?.name ?? ""}
        availableLabel={officeWarning ? `${money2(officeWarning.available)} ${officeWarning.symbol}` : ""}
        requiredLabel={officeWarning ? `${money2(decAdd(officeWarning.available, officeWarning.surplus))} ${officeWarning.symbol}` : ""}
        restLabel={officeWarning ? `${money2(officeWarning.surplus)} ${officeWarning.symbol}` : ""}
        onConfirm={confirmOffice}
        onCancel={() => setWarning(null)}
      />
      <TransferDuplicateCodeWarning
        open={dupWarning !== null}
        code={dupWarning?.pv.code ?? ""}
        matches={(dupWarning?.pv.duplicateCode ?? []).map((m) => ({ numberLabel: `#${m.number}`, name: m.beneficiaryName, dateLabel: m.createdAt.slice(0, 10) }))}
        onConfirm={() => {
          const w = dupWarning;
          setWarning(null);
          if (w) void commit(w.pv, true, w.print);
        }}
        onCancel={() => setWarning(null)}
      />
      {excess.dialog}
      {/* the PIN dialog (z-[100], old security modal) over the form (z-[110]) and the warnings (z-[120]) */}
      <div className="relative z-[130]">{gateway.pin.dialog}</div>
    </>
  );
}

/** The parties' ledger lines as `AccountRow`s for the C22 excess dialogs (they need kind, currency, owner, balance). */
export function partiesAsAccounts(parties: readonly TransferPartyData[]): AccountRow[] {
  const out: AccountRow[] = [];
  for (const p of parties) {
    for (const [cur, line] of [["IQD", p.iqd], ["USD", p.usd]] as const) {
      out.push({
        id: line.accountId, branchId: "", kind: p.kind === "debt" ? "customer_debt" : "customer_trust", currency: cur, name: p.name, category: null, ownerId: p.personId,
        parentAccountId: null, archived: false, balance: line.balance, quantity: "0", costIqd: "0", reserved: "0", available: line.available, total: line.balance,
      });
    }
  }
  return out;
}
